import React, { useEffect, useState, useCallback } from 'react';
import { FiDownload, FiX, FiShare } from 'react-icons/fi';
import './InstallPrompt.css';

const DISMISS_KEY = 'shopyalley:installPromptDismissedAt';
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const isiOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
// Add-to-Home-Screen only works in real Safari. Inside in-app browsers (Facebook,
// Instagram, Line, TikTok, Pinterest, LinkedIn, Twitter) the hint is misleading,
// so suppress it there. Real Safari sets a recognizable UA token ("Safari/")
// and lacks the in-app browser markers below.
const isiOSSafari = () => {
  const ua = navigator.userAgent;
  if (!isiOS()) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|GSA/.test(ua)) return false;       // other browsers on iOS
  if (/FBAN|FBAV|Instagram|Line\/|TikTok|Pinterest|LinkedInApp|Twitter/.test(ua)) return false;
  return /Safari\//.test(ua);
};
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // Safari-specific signal
  window.navigator.standalone === true;

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < DISMISS_COOLDOWN_MS;
};

/**
 * Floating "Install app" banner.
 *
 * Chrome / Edge / Samsung Internet (Android/Desktop): listens for the
 * `beforeinstallprompt` event and shows a one-tap install button.
 *
 * iOS Safari (no install API): renders friendly instructions to use the
 * Share → "Add to Home Screen" flow.
 *
 * Dismissal is remembered for 7 days so we don't pester returning users.
 */
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (recentlyDismissed()) return;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();          // suppress the mini-infobar
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS doesn't fire `beforeinstallprompt`; show a Safari hint after 5s
    // so we don't blow away their first paint.
    let iosTimer;
    if (isiOSSafari() && !isStandalone()) {
      iosTimer = setTimeout(() => { setIosHint(true); setVisible(true); }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* storage blocked */ }
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div className="install-prompt" role="dialog" aria-label="Install Shopyalley app">
      <button className="install-prompt__close" onClick={dismiss} aria-label="Dismiss install prompt">
        <FiX />
      </button>
      <div className="install-prompt__icon" aria-hidden="true"><FiDownload /></div>
      <div className="install-prompt__body">
        <strong>Install Shopyalley</strong>
        {iosHint ? (
          <p>
            Tap <FiShare aria-label="Share" style={{ verticalAlign: 'middle' }} /> in Safari, then
            <em> Add to Home Screen</em> to install the app.
          </p>
        ) : (
          <p>Get a faster, app-like experience with offline browsing.</p>
        )}
      </div>
      {!iosHint && (
        <button className="install-prompt__cta" onClick={install} type="button">
          Install
        </button>
      )}
    </div>
  );
};

export default InstallPrompt;
